import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import { NavLink, useLocation } from 'react-router-dom';
import { FaHistory, FaExchangeAlt, FaUserTie, FaUserPlus, FaDownload, FaMoneyCheckAlt, FaPlus, FaTachometerAlt, FaBoxOpen, FaCashRegister, FaArrowCircleLeft, FaTruck, FaList, FaBalanceScale, FaUserShield, FaUsers, FaChartBar, FaBars, FaMoneyBillWave, FaUserFriends, FaStore, FaTimes } from 'react-icons/fa';
import { FaShop, FaAngleRight, FaAngleDown, FaAnglesDown, FaListCheck } from "react-icons/fa6";
import { FcDebt, FcMoneyTransfer } from "react-icons/fc";
import { LuNotebookPen, LuNotebookText } from "react-icons/lu";
import { useSidebar } from '../App';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';

const menuItems = [
  { icon: <FaTachometerAlt />, label: 'Dashboard', path: '/' },
  {
    label: 'Ombor',
    icon: <FaStore />,
    children: [
      { icon: <FaPlus />, label: 'Omborxona', path: '/omborxona' },
      { icon: <FaShop />, label: 'Mahsulotlar', path: '/store' },
      { icon: <FaExchangeAlt />, label: 'Ko’chirish', path: '/movestore' },
    ],
  },
  {
    label: 'Details',
    icon: <FaList />,
    children: [
      { icon: <FaBoxOpen />, label: 'Mahsulot yaratish', path: '/mahsulotlar' },
      { icon: <FaTruck />, label: 'Taminlovchilar', path: '/suppliers' },
      { icon: <FaList />, label: 'Kategoriyalar', path: '/kategoriyalar' },
      { icon: <FaBalanceScale />, label: 'Birliklar', path: '/birliklar' },
    ],
  },
  { icon: <FaDownload />, label: 'Import', path: '/xaridlar' },
  { icon: <FaCashRegister />, label: 'Kassa', path: '/kassa' },
  { icon: <FaHistory />, label: 'Sotuv tarixi', path: '/chiqimlar' },
  { icon: <LuNotebookText />, label: 'Bronlar', path: '/booking' },
  {
    label: 'Qarzdorliklar',
    icon: <FaMoneyBillWave />,
    children: [
      { icon: <FcMoneyTransfer />, label: 'Mening Qarzim', path: '/qarzdorliklar' },
      { icon: <FcDebt />, label: 'Ulgurchi haridorlar', path: '/customer3' }
    ]
  },
  {
    label: 'Mijozlar',
    icon: <FaUsers />,
    children: [
      { icon: <FaUserFriends />, label: 'Mijozlar', path: '/mijozlar' },
      { icon: <FaMoneyCheckAlt />, label: 'Kreditlar', path: '/credits' },
    ]
  },
  {
    label: 'Xodimlar',
    icon: <FaUserTie />,
    children: [
      { icon: <FaUserPlus />, label: 'Xodim yaratish', path: '/staffs' },
      { icon: <FaUserShield />, label: 'Ruxsatlar', path: '/permissions' }
    ]
  },
  { icon: <FaChartBar />, label: 'Hisobotlar', path: '/hisobotlar' },
  { icon: <FaAnglesDown />, label: 'Qaytarilgan mahsulot', path: '/refunds' },
  { icon: <LuNotebookPen />, label: 'Inventarizatsiya', path: '/inventory' },

];

const Sidebar = () => {
  const { collapsed, setCollapsed } = useSidebar();
  // Tracks if the mouse is over the sidebar while it is collapsed
  const [isHovering, setIsHovering] = useState(false);
  const [openItems, setOpenItems] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Track if on Kassa page
  const isKassa = location.pathname === "/kassa";

  // Simple logic: show text when not collapsed or when hovering over collapsed sidebar
  const shouldShowText = !collapsed || (collapsed && isHovering);

  const toggleItem = (label) => {
    // Auto-expand sidebar when clicking on a nest
    if (collapsed) {
      setCollapsed(false);
    }

    setOpenItems(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu and update open items when route changes
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }

    const activeParent = menuItems.find(item =>
      item.children?.some(child => location.pathname === child.path)
    );

    if (activeParent) {
      setOpenItems(prev => ({ ...prev, [activeParent.label]: true }));
    } else {
      setOpenItems({});
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.querySelector('.sidebar');
      const menuButton = document.querySelector('.mobile-menu-button');

      if (sidebar && !sidebar.contains(event.target) &&
        menuButton && !menuButton.contains(event.target) &&
        mobileOpen) {
        setMobileOpen(false);
      }
    };

    if (mobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileOpen]);

  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const filteredMenuItems = menuItems.map(item => {
    if (user?.role === 'owner') return item;

    if (item.children) {
      const filteredChildren = item.children.filter(child => {
        return hasPermission(child.path);
      });
      return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
    }

    return hasPermission(item.path) ? item : null;
  }).filter(Boolean);

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="mobile-menu-button d-md-none position-fixed start-0 top-0 m-3 p-2 bg-white border-0 rounded"
        onClick={toggleSidebar}
        style={{ zIndex: 1050 }}
      >
        {mobileOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
      </button>

      {/* Overlay */}
      {mobileOpen && (
        <div
          className="d-md-none position-fixed w-100 h-100 bg-dark opacity-50"
          style={{ zIndex: 1040, top: 0, left: 0 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      <nav
        onMouseEnter={() => !isMobile && collapsed && setIsHovering(true)}
        onMouseLeave={() => !isMobile && setIsHovering(false)}
        className={`bg-white border-end sidebar${collapsed ? ' collapsed' : ''} ${isMobile ? 'mobile-sidebar' : ''} ${mobileOpen ? 'mobile-open' : ''}${isKassa ? ' kassa-mode' : ''}`}
        style={{
          width: isMobile ? '100vw' : collapsed ? '64px' : '240px',
          transition: 'transform 0.3s ease-in-out, width 0.2s',
          transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
          top: isKassa ? 0 : 64, // header height or 0 for Kassa
          height: isKassa ? '100vh' : 'calc(100vh - 64px)',
          position: 'fixed',
          left: 0,
          zIndex: isKassa ? 1050 : 1040, // Higher z-index on Kassa page
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Always show collapse/expand button */}
        <div className="d-flex justify-content-end p-2">
          <button
            className="btn btn-link p-0"
            onClick={isMobile ? toggleSidebar : () => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isMobile ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>
        <ul className="nav flex-column">
          {filteredMenuItems.map((item) => {
            if (item.children) {
              return (
                <li key={item.label} className="nav-item">
                  <button
                    className={`nav-link d-flex align-items-center rounded w-100${openItems[item.label] ? ' active bg-success text-white' : ' text-dark'}`}
                    style={{ border: 'none', background: 'none', outline: 'none', boxShadow: 'none' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleItem(item.label);
                    }}
                    aria-expanded={!!openItems[item.label]}
                  >
                    <span className="me-2" style={{ fontSize: '1.3rem', minWidth: 24, textAlign: 'center' }}>{item.icon}</span>
                    {/* Show label when sidebar is expanded or when hovering over collapsed sidebar */}
                    {shouldShowText && <span>{item.label}</span>}
                    {shouldShowText && <span className="ms-auto">{openItems[item.label] ? <FaAngleDown /> : <FaAngleRight />}</span>}
                  </button>
                  <ul
                    className={`nav flex-column ms-3 submenu-collapse ${openItems[item.label] && shouldShowText ? 'show' : ''}`}
                    style={{
                      pointerEvents: openItems[item.label] && shouldShowText ? 'auto' : 'none',
                      transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s, transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                      transform: openItems[item.label] && shouldShowText ? 'translateX(0)' : 'translateX(-24px)',
                      opacity: openItems[item.label] && shouldShowText ? 1 : 0,
                    }}
                  >
                    {item.children.map((child) => (
                      <li key={child.label} className="nav-item">
                        <NavLink
                          to={child.path}
                          className={({ isActive }) =>
                            `nav-link d-flex align-items-center rounded${isActive ? ' active bg-success text-white' : ' text-dark'}`
                          }
                        >
                          <span className="me-2" style={{ fontSize: '1.1rem', minWidth: 24, textAlign: 'center' }}>{child.icon}</span>
                          <span>{child.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>

                </li>
              );
            }
            return (
              <li key={item.label} className="nav-item">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `nav-link d-flex align-items-center rounded${isActive ? ' active bg-success text-white' : ' text-dark'}`
                  }
                  end={item.path === '/'}
                >
                  <span className="me-2" style={{ fontSize: '1.3rem', minWidth: 24, textAlign: 'center' }}>{item.icon}</span>
                  {/* Show label when sidebar is expanded or when hovering over collapsed sidebar */}
                  {shouldShowText && <span>{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default Sidebar; 
